/**
 * arbi contacts              — list contacts
 * arbi contacts add [emails] — add contacts (prompt if no emails)
 * arbi contacts remove [ids] — remove contacts (picker if no IDs)
 */

import { Command } from 'commander'
import { contacts as contactOps } from '@arbidocs/sdk'
import {
  runAction,
  resolveAuth,
  printTable,
  printJson,
  requireInteractive,
  dryRun,
  formatUserName,
  type UserInfo,
} from '../helpers.js'
import { promptCheckbox, promptInput } from '../prompts.js'
import { success } from '../output.js'

/**
 * Strip the giant base64 `picture` field from each contact's embedded user
 * record. The full image is rarely useful for an agent (5 KB+ per contact)
 * and a single piped `arbi contacts --json | jq` was unreadable. Callers
 * who need the bytes can pass `--include-pictures`.
 */
function redactPictures<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map((row) => {
    const user = row.user as Record<string, unknown> | null | undefined
    if (!user || typeof user.picture !== 'string' || user.picture.length === 0) return row
    return {
      ...row,
      user: {
        ...user,
        has_picture: true,
        picture: null,
      },
    } as T
  })
}

export function registerContactsCommand(program: Command): void {
  const contacts = program.command('contacts').description('Contacts: list, add, remove')

  contacts
    .command('list')
    .description('List all contacts')
    .option('--json', 'Output as JSON')
    .option('--include-pictures', 'Keep base64 profile-picture bytes in --json output')
    .action((opts: { json?: boolean; includePictures?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        const data = await contactOps.listContacts(arbi)

        if (opts.json) {
          // Default: drop the inline base64 picture. Opt-in keeps the bytes
          // for the rare workflow that wants the avatar (download tool, etc).
          printJson(opts.includePictures ? data : redactPictures(data))
          return
        }

        if (data.length === 0) {
          process.stderr.write('No contacts found.\n')
          return
        }

        printTable(
          [
            { header: 'ID', width: 16, value: (r) => r.external_id as string },
            {
              header: 'NAME',
              width: 20,
              value: (r) => formatUserName(r.user as UserInfo | null),
            },
            { header: 'EMAIL', width: 30, value: (r) => r.email as string },
            { header: 'STATUS', width: 18, value: (r) => r.status as string },
          ],
          data
        )
      })()
    )

  contacts
    .command('add [emails...]')
    .description('Add contacts by email (prompt if no emails given)')
    .action((emails?: string[]) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        if (!emails || emails.length === 0) {
          requireInteractive('Pass email(s) as positional args: arbi contacts add foo@x.y bar@x.y')
          const input = await promptInput('Email address(es), comma-separated')
          emails = input
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean)
          if (emails.length === 0) return
        }
        const data = await contactOps.addContacts(arbi, emails)
        // Agents extract IDs from the text:
        //   arbi contacts add … | grep -oE 'cnt-[a-z0-9]+'
        for (const c of data) {
          success(`Added: ${c.email} (${c.external_id}) — ${c.status}`)
        }
      })()
    )

  contacts
    .command('remove [ids...]')
    .description('Remove contacts (interactive picker if no IDs given)')
    .option('--dry-run', 'Preview which contacts would be removed (no SDK call)')
    .action((ids: string[] | undefined, opts: { dryRun?: boolean }) =>
      runAction(async () => {
        const { arbi } = await resolveAuth()
        let contactIds = ids && ids.length > 0 ? ids : undefined
        if (!contactIds) {
          const data = await contactOps.listContacts(arbi)
          if (data.length === 0) {
            process.stderr.write('No contacts found.\n')
            return
          }
          requireInteractive('Pass contact IDs directly: arbi contacts remove cnt-…')
          contactIds = await promptCheckbox(
            'Select contacts to remove',
            data.map((c) => {
              const name = formatUserName(c.user as UserInfo | null)
              return {
                name: name ? `${name} (${c.email})` : (c.email as string),
                value: c.external_id as string,
              }
            })
          )
          if (contactIds.length === 0) return
        }
        if (opts?.dryRun) {
          dryRun(`remove ${contactIds.length} contact(s)`, contactIds)
          return
        }
        await contactOps.removeContacts(arbi, contactIds)
        success(`Removed ${contactIds.length} contact(s).`)
      })()
    )

  // Default action: list. Forward raw argv tail rather than pre-declaring
  // --json on the parent — see comment in tags.ts for the commander-binding
  // bug that motivates this pattern. allowUnknownOption keeps the parent
  // shorthand (e.g. `arbi contacts --json`) working.
  contacts
    .allowUnknownOption(true)
    .allowExcessArguments(true)
    .action(async () => {
      const tail = contacts.args ?? []
      await contacts.commands.find((c) => c.name() === 'list')!.parseAsync(tail, { from: 'user' })
    })
}
